import React, { useState } from "react";
import {
  StyleSheet,
  View,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Modal,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedToolTip } from "../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";

export interface IThemedTableEditorProps {
  headers: string[];
  rows: string[][];
  editable?: boolean;
  align?: "left" | "right" | "center";
  draggable?: boolean;
  onDragStart?: (e: any) => void;
  onDragEnd?: (e: any) => void;
  onChange?: (data: { headers: string[]; rows: string[][] }) => void;
  onDeleteTable?: () => void;
}

/**
 * Interactive Table Editor & Viewer.
 *
 * Provides intuitive, zero-keyboard table manipulation:
 * - Table width is strictly limited to the number of columns.
 * - Outer popup button on right edge: "+ Col" to append a column.
 * - Outer popup button on bottom edge: "+ Row" to append a row.
 * - Kebab options (...) on columns and rows to insert or delete rows/columns.
 * - Clean, normal text editing inside every cell without raw HTML markup.
 */
export const ThemedTableEditor: React.FC<IThemedTableEditorProps> = ({
  headers = ["Column 1", "Column 2"],
  rows = [["", ""]],
  editable = true,
  align = "left",
  draggable = false,
  onDragStart,
  onDragEnd,
  onChange,
  onDeleteTable,
}) => {
  const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(null);
  const [menuTarget, setMenuTarget] = useState<{
    type: "column" | "row" | "table";
    index: number;
  } | null>(null);

  // Compute table width limited strictly to number of columns
  const colWidth = 140;
  const tableContentWidth = headers.length * colWidth + (editable ? 32 + 38 : 0);

  // Theme tokens
  const borderColor = useThemeColor({}, "border");
  const cardBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const tintColor = useThemeColor({}, "tint");
  const isDark = cardBg === "#131520";
  const hoverBg = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)";
  const headerBg = isDark ? "#1A1D2B" : "#F3F4F6";

  // Update cell text
  const updateCell = (rowIndex: number, colIndex: number, text: string) => {
    if (!onChange) return;
    if (rowIndex === -1) {
      // Header cell
      const newHeaders = [...headers];
      newHeaders[colIndex] = text;
      onChange({ headers: newHeaders, rows });
    } else {
      // Body cell
      const newRows = rows.map((r, rIdx) => {
        if (rIdx !== rowIndex) return r;
        const newRow = [...r];
        newRow[colIndex] = text;
        return newRow;
      });
      onChange({ headers, rows: newRows });
    }
  };

  // Add column to the right
  const addColumn = (insertAt?: number) => {
    if (!onChange) return;
    const targetIdx = typeof insertAt === "number" ? insertAt : headers.length;
    const newHeaders = [...headers];
    newHeaders.splice(targetIdx, 0, `Col ${targetIdx + 1}`);

    const newRows = rows.map((r) => {
      const copy = [...r];
      copy.splice(targetIdx, 0, "");
      return copy;
    });

    onChange({ headers: newHeaders, rows: newRows });
    setMenuTarget(null);
  };

  // Delete column
  const deleteColumn = (colIndex: number) => {
    if (!onChange || headers.length <= 1) return;
    const newHeaders = headers.filter((_, idx) => idx !== colIndex);
    const newRows = rows.map((r) => r.filter((_, idx) => idx !== colIndex));
    onChange({ headers: newHeaders, rows: newRows });
    setMenuTarget(null);
  };

  // Add row
  const addRow = (insertAt?: number) => {
    if (!onChange) return;
    const targetIdx = typeof insertAt === "number" ? insertAt : rows.length;
    const emptyRow = new Array(headers.length).fill("");
    const newRows = [...rows];
    newRows.splice(targetIdx, 0, emptyRow);
    onChange({ headers, rows: newRows });
    setMenuTarget(null);
  };

  // Delete row
  const deleteRow = (rowIndex: number) => {
    if (!onChange || rows.length <= 1) return;
    const newRows = rows.filter((_, idx) => idx !== rowIndex);
    onChange({ headers, rows: newRows });
    setMenuTarget(null);
  };

  return (
    <View
      style={[
        styles.container,
        {
          borderColor,
          width: tableContentWidth,
          maxWidth: tableContentWidth,
          alignSelf: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
        },
      ]}
      {...(Platform.OS === "web" && draggable
        ? ({
            draggable: true,
            onDragStart,
            onDragEnd,
          } as any)
        : {})}
    >
      {/* Top Table Bar */}
      {editable && (
        <View style={[styles.tableTopBar, { borderColor }]}>
          <View style={styles.tableInfoRow}>
            <Ionicons name="grid-outline" size={14} color={tintColor} style={{ marginRight: 6 }} />
            <ThemedText style={styles.tableInfoText}>
              Table ({headers.length} cols × {rows.length} rows)
            </ThemedText>
          </View>
          {onDeleteTable && (
            <TouchableOpacity
              onPress={onDeleteTable}
              style={styles.deleteTableBtn}
              accessibilityRole="button"
              accessibilityLabel="Delete table"
            >
              <Ionicons name="trash-outline" size={14} color="#EF4444" style={{ marginRight: 4 }} />
              <ThemedText style={styles.deleteTableText}>Delete Table</ThemedText>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Main Grid with Outer Column & Row Popups */}
      <View style={styles.gridOuterWrapper}>
        <ScrollView horizontal showsHorizontalScrollIndicator={true} style={styles.tableScroll}>
          <View style={styles.tableMatrix}>
            {/* Table Header Row */}
            <View style={styles.tableRow}>
              {/* Row handle header spacer */}
              {editable && <View style={[styles.cornerSpacer, { borderColor }]} />}

              {headers.map((h, colIdx) => {
                const isHeaderFocused =
                  activeCell?.row === -1 && activeCell?.col === colIdx;

                return (
                  <View
                    key={`th-${colIdx}`}
                    style={[
                      styles.headerCellWrapper,
                      {
                        borderColor: isHeaderFocused ? tintColor : borderColor,
                        backgroundColor: isHeaderFocused ? hoverBg : headerBg,
                      },
                    ]}
                  >
                    {editable ? (
                      <View style={styles.cellInputRow}>
                        <TextInput
                          value={h}
                          onChangeText={(txt) => updateCell(-1, colIdx, txt)}
                          onFocus={() => setActiveCell({ row: -1, col: colIdx })}
                          onBlur={() => setActiveCell(null)}
                          placeholder={`Col ${colIdx + 1}`}
                          placeholderTextColor={textMuted}
                          style={[styles.headerInput, { color: textColor }]}
                        />
                        <TouchableOpacity
                          onPress={() => setMenuTarget({ type: "column", index: colIdx })}
                          style={styles.kebabBtn}
                          accessibilityRole="button"
                          accessibilityLabel={`Column ${colIdx + 1} options`}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                          <Ionicons name="ellipsis-vertical" size={13} color={textMuted} />
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <ThemedText style={styles.headerText} numberOfLines={1}>{h}</ThemedText>
                    )}
                  </View>
                );
              })}
            </View>

            {/* Table Data Rows */}
            {rows.map((row, rowIdx) => (
              <View key={`tr-${rowIdx}`} style={styles.tableRow}>
                {/* Row Kebab Handle */}
                {editable && (
                  <View style={[styles.rowHandleCell, { borderColor, backgroundColor: headerBg }]}>
                    <TouchableOpacity
                      onPress={() => setMenuTarget({ type: "row", index: rowIdx })}
                      style={styles.rowKebabBtn}
                      accessibilityRole="button"
                      accessibilityLabel={`Row ${rowIdx + 1} options`}
                    >
                      <Ionicons name="ellipsis-horizontal" size={13} color={textMuted} />
                    </TouchableOpacity>
                  </View>
                )}

                {row.map((cellText, colIdx) => {
                  const isFocused =
                    activeCell?.row === rowIdx && activeCell?.col === colIdx;

                  return (
                    <View
                      key={`td-${rowIdx}-${colIdx}`}
                      style={[
                        styles.dataCellWrapper,
                        {
                          borderColor: isFocused ? tintColor : borderColor,
                          backgroundColor: isFocused ? hoverBg : "transparent",
                        },
                      ]}
                    >
                      {editable ? (
                        <TextInput
                          value={cellText}
                          onChangeText={(txt) => updateCell(rowIdx, colIdx, txt)}
                          onFocus={() => setActiveCell({ row: rowIdx, col: colIdx })}
                          onBlur={() => setActiveCell(null)}
                          placeholder=""
                          placeholderTextColor={textMuted}
                          style={[styles.cellInput, { color: textColor }]}
                        />
                      ) : (
                        <ThemedText style={[styles.cellText, { color: textColor }]}>
                          {cellText || "—"}
                        </ThemedText>
                      )}
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        </ScrollView>

        {/* Outer Right Popup Button: + Column */}
        {editable && (
          <View style={styles.outerColumnPopupContainer}>
            <ThemedToolTip text="Add another column">
              <TouchableOpacity
                onPress={() => addColumn()}
                style={[styles.outerAddColBtn, { backgroundColor: cardBg, borderColor: tintColor }]}
                accessibilityRole="button"
                accessibilityLabel="Add another column"
              >
                <Ionicons name="add-circle" size={17} color={tintColor} />
                <ThemedText style={[styles.outerAddColText, { color: tintColor }]}>+ Col</ThemedText>
              </TouchableOpacity>
            </ThemedToolTip>
          </View>
        )}
      </View>

      {/* Outer Bottom Popup Button: + Row */}
      {editable && (
        <View style={styles.outerRowPopupContainer}>
          <ThemedToolTip text="Add another row">
            <TouchableOpacity
              onPress={() => addRow()}
              style={[styles.outerAddRowBtn, { backgroundColor: cardBg, borderColor: tintColor }]}
              accessibilityRole="button"
              accessibilityLabel="Add another row"
            >
              <Ionicons name="add-circle" size={17} color={tintColor} />
              <ThemedText style={[styles.outerAddRowText, { color: tintColor }]}>+ Add Row</ThemedText>
            </TouchableOpacity>
          </ThemedToolTip>
        </View>
      )}

      {/* Active Cell Contextual Control Bar */}
      {editable && activeCell && (
        <View style={[styles.activeCellBar, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.activeCellInfo}>
            <Ionicons name="apps-outline" size={13} color={tintColor} style={{ marginRight: 4 }} />
            <ThemedText style={styles.activeCellText}>
              Inside Table: {activeCell.row === -1 ? `Header Col ${activeCell.col + 1}` : `Row ${activeCell.row + 1}, Col ${activeCell.col + 1}`}
            </ThemedText>
          </View>
          <View style={styles.activeCellActions}>
            <TouchableOpacity
              onPress={() => setMenuTarget({ type: "column", index: activeCell.col })}
              style={[styles.quickKebabBtn, { borderColor }]}
              accessibilityRole="button"
              accessibilityLabel="Column kebab options"
            >
              <Ionicons name="ellipsis-vertical" size={13} color={tintColor} style={{ marginRight: 3 }} />
              <ThemedText style={styles.quickKebabText}>Col Options</ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => deleteColumn(activeCell.col)}
              disabled={headers.length <= 1}
              style={[styles.quickDangerBtn, headers.length <= 1 && styles.disabledBtn]}
              accessibilityRole="button"
              accessibilityLabel="Remove this column"
            >
              <Ionicons name="trash-outline" size={12} color="#EF4444" style={{ marginRight: 3 }} />
              <ThemedText style={styles.quickDangerText}>Remove Column</ThemedText>
            </TouchableOpacity>

            {activeCell.row !== -1 && (
              <>
                <TouchableOpacity
                  onPress={() => setMenuTarget({ type: "row", index: activeCell.row })}
                  style={[styles.quickKebabBtn, { borderColor }]}
                  accessibilityRole="button"
                  accessibilityLabel="Row kebab options"
                >
                  <Ionicons name="ellipsis-horizontal" size={13} color={tintColor} style={{ marginRight: 3 }} />
                  <ThemedText style={styles.quickKebabText}>Row Options</ThemedText>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={() => deleteRow(activeCell.row)}
                  disabled={rows.length <= 1}
                  style={[styles.quickDangerBtn, rows.length <= 1 && styles.disabledBtn]}
                  accessibilityRole="button"
                  accessibilityLabel="Remove this row"
                >
                  <Ionicons name="trash-outline" size={12} color="#EF4444" style={{ marginRight: 3 }} />
                  <ThemedText style={styles.quickDangerText}>Remove Row</ThemedText>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      )}

      {/* Column / Row Context Kebab Modal */}
      {menuTarget && (
        <Modal
          visible={!!menuTarget}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setMenuTarget(null)}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setMenuTarget(null)}
          >
            <View style={[styles.menuPopover, { backgroundColor: cardBg, borderColor }]}>
              {menuTarget.type === "column" && (
                <>
                  <View style={styles.menuTitleRow}>
                    <Ionicons name="reorder-three" size={15} color={tintColor} />
                    <ThemedText style={styles.menuTitle}>
                      Column {menuTarget.index + 1} Actions
                    </ThemedText>
                  </View>
                  <TouchableOpacity
                    style={styles.menuItem}
                    onPress={() => addColumn(menuTarget.index)}
                  >
                    <Ionicons name="arrow-back-outline" size={15} color={textColor} />
                    <ThemedText style={styles.menuItemText}>Insert Column Left</ThemedText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.menuItem}
                    onPress={() => addColumn(menuTarget.index + 1)}
                  >
                    <Ionicons name="arrow-forward-outline" size={15} color={textColor} />
                    <ThemedText style={styles.menuItemText}>Insert Column Right</ThemedText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.menuItem, styles.menuItemDanger]}
                    onPress={() => deleteColumn(menuTarget.index)}
                    disabled={headers.length <= 1}
                  >
                    <Ionicons name="trash-outline" size={15} color="#EF4444" />
                    <ThemedText style={[styles.menuItemText, { color: "#EF4444" }]}>
                      Remove This Column
                    </ThemedText>
                  </TouchableOpacity>
                </>
              )}

              {menuTarget.type === "row" && (
                <>
                  <View style={styles.menuTitleRow}>
                    <Ionicons name="menu" size={15} color={tintColor} />
                    <ThemedText style={styles.menuTitle}>
                      Row {menuTarget.index + 1} Actions
                    </ThemedText>
                  </View>
                  <TouchableOpacity
                    style={styles.menuItem}
                    onPress={() => addRow(menuTarget.index)}
                  >
                    <Ionicons name="arrow-up-outline" size={15} color={textColor} />
                    <ThemedText style={styles.menuItemText}>Insert Row Above</ThemedText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.menuItem}
                    onPress={() => addRow(menuTarget.index + 1)}
                  >
                    <Ionicons name="arrow-down-outline" size={15} color={textColor} />
                    <ThemedText style={styles.menuItemText}>Insert Row Below</ThemedText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.menuItem, styles.menuItemDanger]}
                    onPress={() => deleteRow(menuTarget.index)}
                    disabled={rows.length <= 1}
                  >
                    <Ionicons name="trash-outline" size={15} color="#EF4444" />
                    <ThemedText style={[styles.menuItemText, { color: "#EF4444" }]}>
                      Remove This Row
                    </ThemedText>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </TouchableOpacity>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 10,
    borderWidth: 1,
    borderRadius: 8,
    overflow: "hidden",
  },
  tableTopBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderBottomWidth: 1,
  },
  tableInfoRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  tableInfoText: {
    fontSize: 12,
    fontWeight: "600",
    opacity: 0.8,
  },
  deleteTableBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    backgroundColor: "rgba(239, 68, 68, 0.1)",
  },
  deleteTableText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#EF4444",
  },
  gridOuterWrapper: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  tableScroll: {
    flex: 1,
  },
  tableMatrix: {
    flexDirection: "column",
  },
  tableRow: {
    flexDirection: "row",
  },
  cornerSpacer: {
    width: 32,
    borderRightWidth: 1,
    borderBottomWidth: 1,
  },
  rowHandleCell: {
    width: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRightWidth: 1,
    borderBottomWidth: 1,
  },
  rowKebabBtn: {
    padding: 6,
  },
  headerCellWrapper: {
    width: 140,
    minHeight: 36,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    justifyContent: "center",
    overflow: "hidden",
  },
  cellInputRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  headerInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: "700",
    paddingVertical: 2,
    paddingHorizontal: 2,
    borderWidth: 0,
    backgroundColor: "transparent",
    ...Platform.select({
      web: {
        outlineStyle: "none",
        outlineWidth: 0,
      } as any,
      default: {},
    }),
  },
  kebabBtn: {
    padding: 4,
    marginLeft: 4,
    flexShrink: 0,
  },
  headerText: {
    fontSize: 13,
    fontWeight: "700",
  },
  dataCellWrapper: {
    width: 140,
    minHeight: 38,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    justifyContent: "center",
    overflow: "hidden",
  },
  cellInput: {
    width: "100%",
    minWidth: 0,
    fontSize: 13,
    paddingVertical: 2,
    paddingHorizontal: 2,
    borderWidth: 0,
    backgroundColor: "transparent",
    ...Platform.select({
      web: {
        outlineStyle: "none",
        outlineWidth: 0,
      } as any,
      default: {},
    }),
  },
  cellText: {
    fontSize: 13,
  },
  outerColumnPopupContainer: {
    paddingHorizontal: 6,
    paddingVertical: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  outerAddColBtn: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1.5,
    borderStyle: "dashed",
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 8,
    minWidth: 38,
  },
  outerAddColText: {
    fontSize: 10,
    fontWeight: "700",
    marginTop: 2,
  },
  outerRowPopupContainer: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignItems: "flex-start",
  },
  outerAddRowBtn: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1.5,
    borderStyle: "dashed",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  outerAddRowText: {
    fontSize: 12,
    fontWeight: "600",
    marginLeft: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    alignItems: "center",
    justifyContent: "center",
  },
  menuPopover: {
    width: 230,
    borderWidth: 1,
    borderRadius: 10,
    padding: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 8,
  },
  menuTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(150, 150, 150, 0.2)",
  },
  menuTitle: {
    fontSize: 12,
    fontWeight: "700",
    marginLeft: 6,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: 6,
  },
  menuItemDanger: {
    marginTop: 4,
  },
  menuItemText: {
    fontSize: 13,
    marginLeft: 8,
  },
  activeCellBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 6,
    flexWrap: "wrap",
    gap: 6,
  },
  activeCellInfo: {
    flexDirection: "row",
    alignItems: "center",
  },
  activeCellText: {
    fontSize: 11,
    fontWeight: "700",
    opacity: 0.85,
  },
  activeCellActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  quickKebabBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
  },
  quickKebabText: {
    fontSize: 10,
    fontWeight: "600",
  },
  quickDangerBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
    backgroundColor: "rgba(239, 68, 68, 0.1)",
  },
  quickDangerText: {
    fontSize: 10,
    fontWeight: "600",
    color: "#EF4444",
  },
  disabledBtn: {
    opacity: 0.4,
  },
});

